import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { dbUpdate } from '@/lib/mock/db';
import { queryKeys } from '@/lib/queryKeys';
import type { Site } from '@/lib/types';
import type { DocumentFile } from '@/pages/docs/useDocuments';

/**
 * 현장 조회(PMIS)의 데이터 접근 — 실제 백엔드 `menu/pmis`(`/api/v1/pmis/*`).
 *
 * <b>목 저장소를 쓰지 않는다.</b> 일보·기성·검측은 현장이 매일 쌓는 운영 기록이라 새로고침하면
 * 사라지는 자리에 둘 수 없다. 대신 <b>모든 조회가 현장 하나로 좁혀</b> 나간다(`?siteId=`) —
 * 서버가 그 현장 하나로 소속 격리(계층2)를 판정하기 때문이다.
 *
 * 캐시 키도 현장별로 나눈다. 현장을 바꾸면 이전 현장의 목록이 잠깐 비치는 일이 없어야 한다.
 */

// ── 타입 ─────────────────────────────────────────────────────────────────────

export type PmisDailyLogStatus = 'DRAFT' | 'SUBMITTED';

/** 일보의 공정별 실적 한 줄. 제출하면 이 줄들이 공정율 스냅샷으로 접힌다(DOC-29 §4). */
export type PmisDailyLogItem = {
  id: string;
  wbsItemId: string;
  wbsCode: string;
  wbsName: string;
  /** 그날 한 물량. 누계가 아니다. */
  quantity: number | null;
  unit: string | null;
  /** 계획 물량이 없는 공정에서 직접 적는 그 날짜의 진행율 %. */
  progressRate: number | null;
  note: string | null;
  source: 'MANUAL' | 'AI_DRAFT';
  sortOrder: number;
};

export type PmisDailyLog = {
  id: string;
  siteId: string;
  logDate: string;
  weather: string | null;
  temperature: string | null;
  manpowerCount: number | null;
  equipmentSummary: string | null;
  todayWork: string | null;
  tomorrowPlan: string | null;
  progressNote: string | null;
  /** DRAFT 인 동안에는 실적을 적어도 공정율이 움직이지 않는다. */
  status: PmisDailyLogStatus;
  submittedAt: string | null;
  items: PmisDailyLogItem[];
  /** 그날 출역일보 인원 합계(서버 계산). 일보에 적은 인원과 어긋나면 화면이 그것을 보여 준다. */
  laborTotal: number;
  createdAt: string;
};

/**
 * 제출 프리뷰 한 줄 — "이 공정이 얼마에서 얼마가 되는가".
 *
 * `skipReason` 이 있으면 그 공정은 반영되지 않는다(계획물량 없음·수기 값 충돌 등).
 */
export type PmisProgressChange = {
  wbsItemId: string;
  code: string;
  name: string;
  mode: 'QUANTITY' | 'RATE' | 'NONE';
  quantity: number | null;
  cumulativeQuantity: number | null;
  plannedQuantity: number | null;
  unit: string | null;
  currentRate: number | null;
  currentDate: string | null;
  nextRate: number | null;
  conflict: boolean;
  /** 이 일보 날짜가 최신 스냅샷 이후인가. 아니면 기록만 남고 현재 공정율은 그대로다. */
  affectsLatest: boolean;
  skipReason: string | null;
};

/** 일보 칸에 미리 채울 관측 날씨. 못 받아왔으면 `message` 가 이유를 말한다. */
export type PmisSiteWeather = {
  available: boolean;
  weather: string | null;
  temperature: string | null;
  message: string | null;
};

export type PmisProgressPreview = {
  dailyLogId: string;
  logDate: string;
  changes: PmisProgressChange[];
  rollupBefore: PmisProgressRollup;
  rollupAfter: PmisProgressRollup;
};

export type PmisLaborRecord = {
  id: string;
  siteId: string;
  logDate: string;
  companyName: string;
  trade: string | null;
  headcount: number;
  workArea: string | null;
  note: string | null;
  sortOrder: number;
};

export type PmisClaimType = 'CONTRACT' | 'SUBCONTRACT';

/**
 * 수금 세 걸음 (V268) — 현장이 청구하고, 국내영업이 세금계산서를 발행하고, 재무가 입금을 확인한다.
 *
 * <b>화면이 고르는 값이 아니다.</b> 서버가 날짜를 보고 정한다 — 폼에 상태 칸이 없는 이유다.
 * `APPROVED` 는 없어졌다(V268 이 `INVOICED` 로 옮겼다).
 */
export type PmisClaimStatus = 'CLAIMED' | 'INVOICED' | 'RECEIVED';

/**
 * 회차의 갈래 — 준공금 · 기성 · 유보금 · 선급금 · 기타 (사용자 확정 2026-09-18 · V298).
 *
 * <p>번호는 <b>갈래 안에서</b> 센다(V273). 기성만 「N회차」로 읽히고 나머지는 이름이 곧 그 줄의
 * 정체지만, 같은 갈래가 둘 이상이면(기타가 흔하다) 이름 뒤에 번호가 붙는다.
 */
export type PmisClaimKind = 'COMPLETION' | 'PROGRESS' | 'RETENTION' | 'ADVANCE' | 'OTHER';

export type PmisPaymentClaim = {
  id: string;
  siteId: string;
  /** CONTRACT = 발주처에서 받는 도급기성, SUBCONTRACT = 협력업체가 제출한 하도급기성. */
  claimType: PmisClaimType;
  /** 하도급이면 협력업체명, 도급이면 빈 문자열. */
  vendorName: string;
  /** 공종(건축·전기·통신·소방…). 공무 표가 진행률의 근거로 읽는다(DOC-38 §5). 옛 기성은 null */
  trade: string | null;
  claimKind: PmisClaimKind;
  claimSeq: number;
  /**
   * 회차 보조번호 — 「1-1」 의 뒤엣것. 0 이면 보조번호가 없다(V302).
   *
   * <p>1회차에 대한 <b>마이너스 세금계산서</b>처럼 앞 회차에 딸린 줄이 여기 앉는다. 사람은 한 칸에
   * 「1-1」 을 적고 화면이 두 수로 쪼개 보낸다 — 유일성·정렬은 그대로 숫자다.
   */
  claimSubSeq: number;
  periodYm: string | null;
  /** 기성기준일 — 청구일과 다르다. "26년 9월분을 8월 말 기준으로" 의 뒤엣것 */
  baseDate: string | null;
  claimDate: string | null;
  claimAmount: number | null;
  cumulativeAmount: number | null;
  claimRate: number | null;
  /** 세금계산서 발행일(국내영업). 적히면 상태가 INVOICED 로 오른다 */
  invoiceDate: string | null;
  /**
   * 세금계산서 <b>공급가액</b>(VAT 제외) — 사람이 적은 값이다(V298).
   *
   * <p>발행금액에서 1.1 을 나눠 만들지 않는다. 옛 줄에는 없어 null 이고, 그때만 화면이 1.1 로
   * 물러선다 — 그러면 그 값이 계산된 것임을 알 수 있다.
   */
  invoiceSupplyAmount: number | null;
  /** 발행금액 — VAT 포함. 세금계산서 합계란의 값이다 */
  invoiceAmount: number | null;
  invoiceFileName: string | null;
  /** 저장할 때 <b>되돌려 보내야</b> 하는 값. 안 보내면 첨부가 지워진다(검측과 같은 규약) */
  invoiceFileKey: string | null;
  /**
   * 세금계산서 <b>보기</b> 링크(만료되는 presigned URL). 표의 증빙 아이콘이 이 값으로 선다.
   *
   * <p>키와 다르다 — 키는 저장할 때 되돌려 보내는 값이고, 이것은 열어 보는 주소다.
   * 저장소가 꺼져 있으면 null 이고, 그때는 아이콘이 서지 않는다.
   */
  invoiceFileUrl: string | null;
  /** 수금예정일 — 사람이 덮어쓴 값만 있다. 비면 화면이 계약 수금조건에서 계산한다 */
  dueDate: string | null;
  status: PmisClaimStatus;
  receivedDate: string | null;
  /** 실수금액. 청구액과 다를 수 있다 — 부분수금(사용자 확정 2026-09-14) */
  collectAmount: number | null;
  /** 실수금 사유 — 적혀 있을 때만 표의 실수금액 아래에 선다(V298) */
  collectNote: string | null;
  receiptFileName: string | null;
  receiptFileKey: string | null;
  /** 입금증(하도급은 이체증) 보기 링크. {@link invoiceFileUrl} 과 같은 규약 */
  receiptFileUrl: string | null;
  note: string | null;
};


/**
 * 검측 상태. `RECHECK` 는 <b>V47 이후 새로 붙지 않는다</b> — 재검측은 상태가 아니라 회차다(DOC-29 §3.1).
 * 값이 남아 있는 것은 그 전에 저장된 행들 때문이다.
 */
export type PmisInspectionStatus = 'REQUESTED' | 'IN_REVIEW' | 'PASSED' | 'FAILED' | 'RECHECK';

/** 검측 증빙 사진 한 장. EXIF 세 값은 비어 있는 것이 정상이다(DOC-27 §4.3). */
export type PmisInspectionPhoto = {
  id: string;
  fileName: string | null;
  /** 보기 링크(presigned). 저장소가 없으면 null. */
  url: string | null;
  shotAt: string | null;
  lat: number | null;
  lon: number | null;
  qualityProfile: string | null;
  /** OVERVIEW/CLOSEUP/MEASURE/LABEL/ETC — 무엇을 찍은 사진인가. */
  shotKind: string;
  aiStatus: 'PENDING' | 'ANALYZING' | 'ANALYZED' | 'SKIPPED' | 'FAILED';
  aiNote: string | null;
  sortOrder: number;
  /**
   * 증빙 무결성 경고. <b>상세 조회에서만</b> 채워진다(목록에서는 전부 false).
   * 저장된 값이 아니라 서버가 조회할 때 계산한 것이다 — 임계값이 바뀌면 경고도 바뀐다.
   */
  evidence: {
    /** 사실상 같은 사진(먼저 올라온 것)의 id — 재검에 1차 사진을 다시 올렸다는 뜻. */
    duplicateOf: string | null;
    shotBeforeRework: boolean;
    distanceMeters: number | null;
    farFromSite: boolean;
    noShotTime: boolean;
    suspicious: boolean;
  } | null;
};

/** 검측 사건 한 줄. 고치는 경로가 없다 — 이력은 쌓기만 한다. */
export type PmisInspectionEvent = {
  id: string;
  roundNo: number;
  eventType:
    | 'REQUESTED'
    | 'ASSIGNED'
    | 'STARTED'
    | 'PASSED'
    | 'FAILED'
    | 'RECHECK_REQUESTED'
    | 'PHOTO_ADDED'
    | 'AI_DRAFTED';
  actorName: string | null;
  note: string | null;
  occurredAt: string;
};

export type PmisInspection = {
  id: string;
  siteId: string;
  inspectionDate: string | null;
  inspectionType: string | null;
  trade: string | null;
  title: string;
  location: string | null;
  requester: string | null;
  inspector: string | null;
  status: PmisInspectionStatus;
  resultNote: string | null;
  attachmentName: string | null;
  /** 저장소 오브젝트 키(URL 아님). 첨부를 바꾸지 않은 수정에서 그대로 되돌려 보낸다. */
  attachmentKey: string | null;
  /** 만료되는 presigned URL. 저장소가 없으면 null. */
  attachmentUrl: string | null;
  wbsItemId: string | null;
  wbsCode: string | null;
  wbsName: string | null;
  /** 회차. 재검측은 앞 회차를 덮지 않고 이 번호를 올린 새 행이 된다. */
  roundNo: number;
  parentId: string | null;
  requesterId: string | null;
  inspectorId: string | null;
  dueDate: string | null;
  /** 결함 유형(재발 집계의 축). AI 가 제안하고 사람이 고른다. */
  defectType: string | null;
  photos: PmisInspectionPhoto[];
  /** 상세를 열 때만 채워진다. 목록에서는 빈 배열. */
  events: PmisInspectionEvent[];
  /** "내 검측함" 처럼 현장을 가로지르는 목록에서만 채워진다. */
  siteName: string | null;
};

export type PmisWbsProgress = {
  id: string;
  wbsItemId: string;
  recordDate: string;
  plannedRate: number | null;
  actualRate: number | null;
  note: string | null;
  /** MANUAL = 사람이 입력, DAILY_LOG = 작업일보 제출로 자동 반영. */
  source: 'MANUAL' | 'DAILY_LOG';
};

export type PmisWbsItem = {
  id: string;
  siteId: string;
  parentId: string | null;
  code: string;
  name: string;
  /** 롤업 가중치(비중). 금액이 아니다 — 금액은 `costAmount` 다(V164). */
  weight: number;
  /**
   * 공사비(천원). 착수·완료일과 함께 있으면 <b>월별 배분표</b>에 오른다.
   * 비어 있으면 그 공정은 배분표에서 빠지고, 화면이 그 개수를 말한다.
   */
  costAmount: number | null;
  /** 계획 물량. 있으면 이 공정은 작업일보 실적 누계로 진행율이 자동 산정된다. */
  plannedQuantity: number | null;
  unit: string | null;
  source: 'MANUAL' | 'AI_DRAFT';
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  sortOrder: number;
  /** 하위 공정이 없는가. 롤업에 실제로 들어가는 것은 리프뿐이다. */
  leaf: boolean;
  latestProgress: PmisWbsProgress | null;
  /**
   * <b>오늘 기준 계획상 진행율(%)</b> — 서버가 계획 일정에서 계산한다. 저장된 값이 아니다.
   * 진척 스냅샷이 없어도 예정(%) 칸이 비지 않게 한다. 일정이 없으면 null.
   */
  plannedRateToday: number | null;
};

/**
 * 현장 하나의 가중 롤업. `itemCount === 0` 이면 아직 진척 입력이 없다는 뜻이고,
 * 그때 화면은 주간회의 공정율로 폴백한다.
 */
export type PmisProgressRollup = {
  siteId: string | null;
  plannedRate: number | null;
  actualRate: number | null;
  itemCount: number;
};

export type PmisWbsBoard = {
  siteId: string;
  items: PmisWbsItem[];
  /** 공정별 누적 — 작업일보 근거. 아래 표의 공정 줄들과 같은 자다 */
  rollup: PmisProgressRollup;
  /** 보고 공정율 — 주간회의에 적은 누계. <b>정본</b>이며 현장 정보·공무가 이 값을 쓴다 */
  reportedRate: number | null;
};

export type PmisSiteDocument = {
  id: string;
  siteId: string;
  name: string;
  docType: string | null;
  status: 'PENDING' | 'INDEXED' | 'MAPPED' | 'FAILED';
  fileUrl: string | null;
  /**
   * 삭제를 기다리는 줄인가 (V378).
   *
   * <p>참인 줄은 <b>전산 담당·관리자에게만</b> 내려온다 — 다른 사람에게는 서버가 이미 목록에서
   * 빼고 준다. 그 줄에 「삭제 요청」 표시와 승인·반려 손이 선다.
   */
  deleteRequested: boolean;
  createdAt: string;
};

/**
 * 현장 연락처 한 줄 — 관계사(발주처·감리·설계사)와 배치 인원을 <b>서버가 한 모양으로</b> 준다.
 *
 * <p>이 표는 화면에서 만들었었다(인력·배치를 각각 받아 현장명으로 견줬다). 대화가 같은 값을 묻게
 * 되면서 규칙을 서버로 옮겼다 — 두 벌이면 한쪽만 고쳐지는 날 화면과 대화가 다른 사람을 답한다
 * (2026-08-20 · DOC-53 P1).
 */
export type PmisSiteContact = {
  /** 수첩 줄이면 그 행의 id, 자동으로 모인 줄(관계사·우리 인원)이면 null (V98). */
  id: string | null;
  /**
   * 우리 인력이면 그 사람의 id. 이름을 눌러 직원찾기 명함을 여는 데 쓴다(2026-08-26).
   *
   * <p>이름으로 되짚지 않는다 — 동명이인이 있으면 <b>남의 명함</b>이 열린다.
   */
  personnelId: string | null;
  /** 소속(부서) — 본사 수첩 줄만 값이 있다(V342). 현장 줄의 소속은 묶음의 현장 이름이다. */
  department: string | null;
  /** 발주처 · 감리 · 설계사 · 당사 · 협력업체 … */
  group: string;
  company: string | null;
  name: string | null;
  rank: string | null;
  duty: string | null;
  phone: string | null;
  extension: string | null;
  email: string | null;
  note: string | null;
  /** 이 화면에서 고칠 수 있는 줄인가. 관계사는 현장 수정, 우리 인원은 인력배치표가 정본이다. */
  editable: boolean;
};

/**
 * 연락처 수첩에 적을 수 있는 「구분」. 서버의 `PmisCodes.CONTACT_GROUPS` 와 한 벌이다.
 *
 * 「당사」가 없는 것이 요점이다 — 우리 인원의 정본은 인력배치표이고, 여기서 적으면 두 벌이 된다.
 *
 * <p>뒤의 셋(영업처·금융·보험·관공서)은 <b>본사 수첩</b>이 쓴다(2026-09-22 · V329). 현장 줄과
 * 같은 목록을 쓴다 — 갈라 두면 같은 보증보험 담당자가 적은 자리에 따라 다른 구분을 달게 된다.
 */
export const SITE_CONTACT_GROUPS = [
  '협력업체', '발주처', '감리', '설계사', '인허가', '자재', '장비',
  '영업처', '금융·보험', '관공서', '기타',
] as const;

export type SiteContactPayload = {
  /** 비면 <b>본사</b> 줄이다(V329). 현장 줄은 그 현장 id 를 그대로 싣는다. */
  siteId: string | null;
  /** 소속(부서). 본사 줄에서만 뜻이 있다 — 현장 줄은 서버가 비운다(V342). */
  department?: string | null;
  group: string;
  company: string;
  name?: string | null;
  rank?: string | null;
  duty?: string | null;
  phone?: string | null;
  extension?: string | null;
  email?: string | null;
  note?: string | null;
};

export type PmisOpenChatStatus = 'ACTIVE' | 'ARCHIVED';

/**
 * 현장 오픈채팅방 <b>주소</b> 한 줄(DOC-26). 대화는 실려 오지 않는다 — 카카오에 그런 API 가 없다.
 *
 * `joinCode` 는 상세 조회에서만 채워진다. 목록에서는 언제나 null 이다.
 */
export type PmisOpenChatRoom = {
  id: string;
  siteId: string;
  siteName: string;
  title: string;
  purpose: string | null;
  inviteUrl: string;
  joinCode: string | null;
  managerId: string | null;
  managerName: string | null;
  note: string | null;
  status: PmisOpenChatStatus;
  /** 신고 사유. 비어 있지 않으면 화면이 그 방에 경고를 붙인다. */
  reportReason: string | null;
  createdAt: string;
};

export type OpenChatRoomPayload = {
  siteId: string;
  title: string;
  purpose: string | null;
  inviteUrl: string;
  joinCode: string | null;
  managerId: string | null;
  note: string | null;
  /** 비밀번호를 적을 때만 켠다. 서버가 비밀번호 있는 요청에서 이 값을 본다. */
  managerConsent: boolean;
};

/** 현장 카드 배지용. 방이 없는 현장은 목록에 나오지 않는다. */
export type PmisSiteRoomCount = { siteId: string; roomCount: number };

type UploadTicket = { key: string; url: string };

// ── 캐시 키 ──────────────────────────────────────────────────────────────────

const pmisKeys = {
  wbs: (siteId: string) => ['pmis', 'wbs', siteId] as const,
  wbsCurve: (siteId: string) => ['pmis', 'wbs-curve', siteId] as const,
  /** 공사비 월별 배분표. 공정 트리와 같이 움직이므로 트리를 고치면 함께 무효화한다. */
  wbsCostSpread: (siteId: string) => ['pmis', 'wbs-cost-spread', siteId] as const,
  /** 공정표 한 장(DOC-86). 내려받는 엑셀과 같은 모델이라 공정을 고치면 함께 무효화한다. */
  wbsSheet: (siteId: string) => ['pmis', 'wbs-sheet', siteId] as const,
  /** 전 현장 롤업은 현장 하나에 매이지 않는다 — 현장 정보 보드가 통째로 쓴다. */
  rollups: ['pmis', 'progress-rollups'] as const,
  dailyLogs: (siteId: string) => ['pmis', 'daily-logs', siteId] as const,
  labor: (siteId: string, date: string | null) => ['pmis', 'labor', siteId, date ?? 'all'] as const,
  payments: (siteId: string) => ['pmis', 'payments', siteId] as const,
  inspections: (siteId: string) => ['pmis', 'inspections', siteId] as const,
  documents: (siteId: string) => ['pmis', 'documents', siteId] as const,
  scenery: (siteId: string) => ['pmis', 'scenery', siteId] as const,
  contacts: (siteId: string) => ['pmis', 'contacts', siteId] as const,
  contractSummary: (siteId: string, slot: string) => ['pmis', 'contract-summary', siteId, slot] as const,
  openChat: (siteId: string, status: PmisOpenChatStatus | null) =>
    ['pmis', 'openchat', siteId, status ?? 'all'] as const,
  /** 홈 위젯·현장 카드가 쓰는 것들은 현장 하나에 매이지 않는다. */
  openChatMine: ['pmis', 'openchat', 'mine'] as const,
  openChatCounts: ['pmis', 'openchat', 'counts'] as const,
};

// ── WBS 공정율 ───────────────────────────────────────────────────────────────

export type WbsItemPayload = {
  siteId: string;
  parentId: string | null;
  code: string;
  name: string;
  weight: number | null;
  plannedQuantity: number | null;
  unit: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  /** 공사비(천원). 비우면 null — 0 으로 보내면 배분표에서 "금액 없음" 과 "0원" 이 구분되지 않는다. */
  costAmount: number | null;
};

export type WbsProgressPayload = {
  recordDate: string;
  plannedRate: number | null;
  actualRate: number | null;
  note: string | null;
};

/** 공정 트리 + 각 공정의 최신 진척 + 그 현장 롤업을 한 번에 받는다. */
export function useWbsBoard(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.wbs(siteId),
    queryFn: () => apiFetch<PmisWbsBoard>(`/v1/pmis/wbs?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 공정율 곡선 한 점. <b>`actualRate` 가 null 이면 아직 오지 않은 날</b>이다 — 0 이 아니다.
 */
export type PmisProgressCurvePoint = {
  date: string;
  plannedRate: number;
  /** 공정별 누적(작업일보) */
  actualRate: number | null;
  /** 보고 공정율(주간회의). 두 선이 벌어진 만큼이 아직 일보에 안 올라온 진행분이다 */
  reportedRate: number | null;
};

/**
 * 공정율 곡선 (DOC-33).
 *
 * `datedCount` 는 리프 중 <b>계획 일정이 있는</b> 공정 수다. `leafCount` 보다 적으면 예정 곡선이
 * 100% 에 닿지 않는다 — 숨기지 않고 화면이 그 사실을 말한다.
 */
export type PmisProgressCurve = {
  start: string | null;
  end: string | null;
  points: PmisProgressCurvePoint[];
  leafCount: number;
  datedCount: number;
  /**
   * 예정 곡선이 <b>파일에서 읽은 것</b>인가. false 면 착수·완료일로 만든 S-curve 다.
   * 지어낸 곡선을 계획으로 오해하면 그 차이가 발주처 보고까지 간다 — 화면이 구분해 말한다.
   */
  fromFile: boolean;
};

/**
 * 예정·실적 곡선. <b>서버가 접어서 준다</b> — 화면이 자기 식으로 그리면 표의 롤업 숫자와
 * 그래프의 오늘 지점이 갈라진다.
 */
export function useWbsCurve(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.wbsCurve(siteId),
    queryFn: () => apiFetch<PmisProgressCurve>(`/v1/pmis/wbs/curve?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 배분표 한 줄 — 공정 하나를 계획 일정에 맞춰 쪼갠 금액.
 *
 * `amounts` 는 `periods` 와 <b>길이·순서가 같다</b>. 셀마다 월 이름을 붙이지 않는 이유는
 * 공정 200개 × 36개월이면 그 문자열만 7,200번이기 때문이다.
 */
export type PmisCostSpreadRow = {
  itemId: string;
  code: string;
  name: string;
  costAmount: number;
  plannedStartDate: string;
  plannedEndDate: string;
  amounts: number[];
};

/**
 * <b>공사비 월별 배분표</b> (V164) — 프리마베라의 cost spreading 에 해당한다.
 *
 * <p>배분 곡선은 예정 공정율과 <b>같은 베타(2,2) S-curve</b>다. 서버가 접어서 주므로 화면은
 * 그리기만 한다 — 여기서 다시 쪼개면 그래프의 예정선과 이 표의 누계가 갈라진다.
 *
 * <p>`missingCost`·`missingDate` 는 <b>배분하지 못한 공정 수</b>다. 그 줄을 조용히 빼면
 * 사용자는 합계가 왜 도급액에 못 미치는지 알 수 없다.
 */
export type PmisCostSpread = {
  /** "2026-03" 꼴의 월 이름들 */
  periods: string[];
  rows: PmisCostSpreadRow[];
  monthlyTotal: number[];
  cumulative: number[];
  totalCost: number;
  leafCount: number;
  spreadCount: number;
  missingCost: number;
  missingDate: number;
  /** 기간 상한(360개월)에 걸려 잘렸는가. true 면 마지막 누계가 총액에 못 미친다 */
  truncated: boolean;
  /**
   * 계산에서 <b>뺀 합계 행</b>. 「[ 건축공사 소계 ]」·「직접공사비 계」 처럼 금액이 형제들의 합인
   * 줄은 그대로 두면 같은 돈을 두 번 센다(청주 반도체 공장에서 총액이 2.37배였다).
   *
   * <p>이름으로 가려내므로 <b>잘못 걸러 낼 수 있다</b> — 그래서 화면이 이 목록을 반드시 보인다.
   */
  excluded: PmisAggregateRow[];
};

/** 계산에서 뺀 합계 행 하나. `costAmount` 만큼이 두 번 세어지고 있었다. */
export type PmisAggregateRow = {
  code: string;
  name: string;
  costAmount: number | null;
};

/** 공사비 월별 배분표. 공정·일정을 고치면 함께 다시 받는다. */
export function useWbsCostSpread(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.wbsCostSpread(siteId),
    queryFn: () => apiFetch<PmisCostSpread>(`/v1/pmis/wbs/cost-spread?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 전 현장 공정율 롤업. 현장 정보 보드가 카드마다 질의하지 않도록 한 번에 받는다.
 *
 * 권한 키가 `site_status`(요약)라 현장 조회 권한이 없는 사람에게도 열린다 —
 * "요약은 공개, 상세·편집은 격리"(DOC-17 §2.2).
 */
/**
 * 그 날짜의 <b>계획 누계 공정율</b>(전 현장) — 주간회의 계획 칸의 흐린 글씨.
 *
 * <p>공정표가 이미 아는 값을 사람이 매주 손으로 계산할 이유가 없다. 다만 <b>넣어 주지는 않는다</b> —
 * 계획대로 안 가는 주가 있어야 회의가 열린다.
 *
 * <p>주차마다 키가 갈리므로 화면이 현장 수만큼 불러도 요청은 한 번이다(같은 키는 합쳐진다).
 */
export function useSitePlannedRates(date: string | null | undefined) {
  return useQuery({
    queryKey: ['pmis', 'planned-rates', date] as const,
    queryFn: () => apiFetch<Array<{ siteId: string; plannedRate: number | null }>>(
      `/v1/pmis/progress-planned?date=${date}`,
    ),
    enabled: Boolean(date),
    // 권한이 없으면 조용히 물러선다 — 없어도 화면은 그대로 쓴다(흐린 글씨만 빈다).
    retry: false,
  });
}

export function useProgressRollups() {
  return useQuery({
    queryKey: pmisKeys.rollups,
    queryFn: () => apiFetch<PmisProgressRollup[]>('/v1/pmis/progress-rollups'),
  });
}

export function useWbsMutations(siteId: string) {
  const queryClient = useQueryClient();
  // 공정을 고치면 그 현장 트리와 전 현장 롤업(카드)이 함께 달라진다.
  // 공사비·계획 일정이 바뀌면 월별 배분도 따라 바뀐다 — 표만 고쳐지고 배분이 남으면 둘이 어긋난다.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCostSpread(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
  };

  return {
    createItem: useMutation({
      mutationFn: (payload: WbsItemPayload) =>
        apiFetch<PmisWbsItem>('/v1/pmis/wbs', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    updateItem: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: WbsItemPayload }) =>
        apiFetch<PmisWbsItem>(`/v1/pmis/wbs/${id}`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    removeItem: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/wbs/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    /** 진척 저장은 업서트다 — 같은 날짜면 고치고 다른 날짜면 새 스냅샷이 쌓인다. */
    saveProgress: useMutation({
      mutationFn: ({ itemId, payload }: { itemId: string; payload: WbsProgressPayload }) =>
        apiFetch<PmisWbsProgress>(`/v1/pmis/wbs/${itemId}/progress`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
  };
}

/**
 * 공정표 격자의 한 줄 (DOC-86) — <b>코드가 아니라 경로</b>로 말한다.
 *
 * <p>대 → 중 → 세부 이름을 주면 서버가 상위를 찾거나 만들고 코드를 붙인다. `id` 가 있으면 그 공정을
 * 고치고 없으면 새로 만든다. 사람이 코드를 치지 않는다 — 같은 번호를 두 사람이 적는 일이 없어야 한다.
 */
export type WbsSheetRowPayload = {
  siteId: string;
  id: string | null;
  /** 이 줄이 앉는 칸(0~5). 화면의 계단에서 이름이 놓인 그 자리다. */
  levelDepth: number;
  /** 상위 공정 — 계단에서 바로 위의 더 얕은 줄. 최상위면 null. 이름이 아니라 id 로 보낸다. */
  parentId: string | null;
  name: string;
  costAmount: number | null;
  plannedQuantity: number | null;
  unit: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  /** 공사금액 &gt; 비용구분. */
  costType: string | null;
  /** 공사금액 &gt; 비고. */
  note: string | null;
  /** 대표물량 &gt; 항목. */
  quantityLabel: string | null;
};

export function useWbsSheetRow(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WbsSheetRowPayload) =>
      apiFetch<PmisWbsItem>('/v1/pmis/wbs/sheet-row', { method: 'PUT', body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCostSpread(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
    },
  });
}

/**
 * 간트의 주 열 하나 (DOC-86). 머리글 여섯 줄(년·월·D·누계주차·일·누계일)이 전부 여기서 나온다.
 * `monthSpan`·`yearSpan` 은 첫 칸에만 값이 있다 — 엑셀은 병합, 화면은 `colSpan` 이다.
 */
export type PmisWbsSheetWeek = {
  date: string;
  year: number;
  month: number;
  /** 착공 달을 1로 센 달 번호 — 표본의 `M1`·`M2`. */
  monthIndex: number;
  weekIndex: number;
  day: number;
  cumulativeDay: number;
  firstOfMonth: boolean;
  firstOfYear: boolean;
  monthSpan: number;
  yearSpan: number;
};

/**
 * 공정 한 줄 — <b>리프</b>다. `levelSpan` 은 대공종·중공종 칸이 세로로 몇 줄에 걸치나이고,
 * 그 묶음의 첫 줄이 아니면 0 이다(엑셀은 병합, 화면은 `rowSpan`).
 */
export type PmisWbsSheetRow = {
  itemId: string;
  code: string;
  /** 상위 공정. 줄을 저장할 때 그대로 돌려보낸다 — 자리가 흔들리지 않게. */
  parentId: string | null;
  /** 이 줄이 앉는 칸(0~5). `levels` 에서 값이 있는 그 자리다. */
  levelDepth: number;
  /** WBS0 ~ WBS5. <b>자기 이름 하나</b>만 자기 칸에 들어 있다(계단). */
  levels: string[];
  levelSpan: number[];
  costAmount: number | null;
  share: number | null;
  plannedQuantity: number | null;
  unit: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  /** 착수~완료 <b>일수</b>. 날짜에서 나오므로 적는 칸이 아니다. 하나라도 없으면 null. */
  durationDays: number | null;
  costType: string | null;
  note: string | null;
  quantityLabel: string | null;
  /** <b>실적</b> 착수일 (V272). 계획 막대 아래에 두 번째 막대를 긋는 근거다. */
  actualStartDate: string | null;
  actualEndDate: string | null;
  /** 실적 착수~완료 일수. 날짜에서 나오므로 적는 칸이 아니다. */
  actualDays: number | null;
  /** <b>누계</b> 실적물량. 계획수량으로 나눈 값이 진도율이다. */
  actualQuantity: number | null;
  /** 완료 처리됐나. 되면 진도율은 100% 로 고정되고 그 줄의 실적 칸이 잠긴다. */
  completed: boolean;
  /**
   * <b>주공정(CP)</b> 인가 (V275). 계산한 값이 아니라 공정 담당이 찍은 표시다 —
   * 이 공정표에는 선후행이 없어 임계경로를 낼 수 없다. 간트에서 빨갛게 선다.
   */
  criticalPath: boolean;
  actualRate: number | null;
  /**
   * 수기 값이 막고 있는 작업일보 진도율(2026-10-27). 다를 때만 온다. 수기 실적을 비우면 이 값으로 돌아간다.
   * 재시작 전 서버는 안 보낸다.
   */
  dailyLogRate?: number | null;
  /** 계획상 <b>오늘</b> 몇 % 여야 하나. 목록의 「예정(%)」 이다 — 서버가 읽을 때 계산한다. */
  plannedRateToday: number | null;
  /** 간트 막대가 걸치는 주 열의 처음·끝(0-기준). 일정이 없으면 -1. */
  barFrom: number;
  barTo: number;
  /** <b>실적</b> 막대가 걸치는 주 열의 처음·끝(0-기준). 없으면 -1. */
  actualBarFrom: number;
  actualBarTo: number;
  /*
    ── 목표 (V539) — 재시작 전 서버는 이 칸들을 안 준다. ──
  */
  targetStartDate?: string | null;
  targetEndDate?: string | null;
  /** 목표 착수~완료 일수. 계약의 `durationDays` 와 같은 셈법이다. */
  targetDays?: number | null;
  /** 목표 완료일 − 계약 완료일(일). 음수면 그만큼 당겨 잡은 것이다. */
  targetEndGap?: number | null;
  /** <b>목표</b> 막대가 걸치는 주 열의 처음·끝(0-기준). 없으면 -1. */
  targetBarFrom?: number;
  targetBarTo?: number;
};

/** 집계 블록의 한 달 — 계획·실적 × 월보할·누계보할. 값은 공정율 곡선의 월말이다. */
export type PmisWbsSheetMonth = {
  fromWeek: number;
  toWeek: number;
  year: number;
  month: number;
  planMonthly: number | null;
  planCumulative: number | null;
  actualMonthly: number | null;
  actualCumulative: number | null;
};

/**
 * <b>공정표 한 장</b> (DOC-86) — 화면과 엑셀이 같이 읽는 모델.
 *
 * <p>「공정표 만들기를 누르면 엑셀을 내려받았을 때 만들어지는 자료가 바로 보여야 한다」(2026-09-10).
 * 그래서 화면은 이 모델을 <b>그리기만</b> 한다 — 열을 어디서 끊고 이 달 월보할이 얼마인지를
 * 화면이 다시 정하면 언젠가 받은 파일과 다른 말을 한다.
 */
/** 공정표 <b>기준선</b>(V255). 있으면 그 현장의 공정표는 잠긴 것이다 — 고치려면 설계변경을 거친다. */
export type PmisWbsBaseline = {
  siteId: string;
  revision: number;
  confirmedAt: string;
  confirmedBy: string | null;
};

/**
 * 공정 <b>연계</b> 하나 (V276) — 선행이 끝나야 후행이 시작된다.
 *
 * <p>날짜를 들고 있지 않다. 이 관계로 일정을 다시 계산하지 않기 때문이다 — 담기는 것은
 * 「무엇이 무엇을 기다리나」 뿐이고, 언제인가는 두 공정이 각자 들고 있다.
 */
export type PmisWbsLink = {
  id: string;
  predecessorId: string;
  successorId: string;
};

export type PmisWbsSheet = {
  siteId: string;
  siteName: string;
  location: string;
  periodText: string;
  start: string;
  end: string;
  weeks: PmisWbsSheetWeek[];
  rows: PmisWbsSheetRow[];
  /** 공정 연계(V276). 줄과 <b>한 번에</b> 온다 — 선을 그을 자리가 결국 그 줄들의 막대 자리다. */
  links: PmisWbsLink[];
  /**
   * <b>보여 줄</b> WBS 이름 칸 수 (2026-09-15). `rows[].levels` 는 늘 자리 수만큼(열여섯) 오지만,
   * 그 현장이 실제로 쓰는 깊이 + 여유 한 칸까지만 화면에 선다 — 그 여유 칸에 적으면 다음에
   * 열 때 한 칸이 더 는다.
   */
  levelCount: number;
  totalCost: number | null;
  months: PmisWbsSheetMonth[];
  /** 최종 저장된 표인가. 있으면 읽는 표로 잠긴다. */
  baseline: PmisWbsBaseline | null;
};

/**
 * <b>최종 저장</b> — 지금 공정표를 기준선으로 굳힌다 (DOC-86 §6).
 *
 * <p>이 뒤로 공정 구조를 바꾸는 길이 서버에서 막힌다. 무효화는 공정을 고칠 때와 같은 묶음이다 —
 * 잠긴 사실이 표·곡선·배분표에 한꺼번에 반영돼야 한다.
 */
export function useWbsBaseline(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
  };
  return {
    confirm: useMutation({
      mutationFn: () =>
        apiFetch<PmisWbsBaseline>(`/v1/pmis/wbs/baseline?siteId=${siteId}`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    release: useMutation({
      mutationFn: () => apiFetch<void>(`/v1/pmis/wbs/baseline?siteId=${siteId}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
}

/**
 * 공정표의 <b>실적</b> 한 줄 (V272 · DOC-86 §8) — 실적 착수·완료일과 누계 실적물량.
 *
 * <p>계획을 고치는 `WbsSheetRowPayload` 와 <b>문이 다르다</b>: 실적은 최종 저장(기준선) 뒤에도
 * 쌓이는 값이고, 권한도 공정 구조가 아니라 진척 입력 쪽이다.
 */
export type WbsActualPayload = {
  actualStartDate: string | null;
  actualEndDate: string | null;
  /** <b>누계</b> 실적물량. 그날 한 양이 아니다. */
  actualQuantity: number | null;
};

/**
 * 실적 저장과 <b>완료 처리</b> (V272).
 *
 * <p>무효화 범위가 계획을 고칠 때와 같다 — 실적이 들어가면 진도율이 바뀌고, 그것이 곧 현장
 * 공정율·곡선·현장 정보 카드다. 한쪽만 갱신하면 같은 화면에서 두 숫자가 갈라진다.
 */
export function useWbsActual(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
  };
  return {
    save: useMutation({
      mutationFn: ({ itemId, payload }: { itemId: string; payload: WbsActualPayload }) =>
        apiFetch<PmisWbsItem>(`/v1/pmis/wbs/${itemId}/actual`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    complete: useMutation({
      mutationFn: (itemId: string) =>
        apiFetch<PmisWbsItem>(`/v1/pmis/wbs/${itemId}/complete`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    /** 완료 되돌리기(2026-10-27) — 끊은 것과 같은 수정 권한이 다시 연다. */
    reopen: useMutation({
      mutationFn: (itemId: string) =>
        apiFetch<PmisWbsItem>(`/v1/pmis/wbs/${itemId}/reopen`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
  };
}

/**
 * <b>주공정(CP)</b> 을 찍거나 끈다 (V275 · 사용자 지시 2026-09-15).
 *
 * <p>되돌릴 수 있는 손짓이라 <b>누르면 곧장 나간다</b> — 실적처럼 [저장] 을 기다리지 않는다.
 * CP 는 값이 아니라 표시라 「무엇이 굳는가」 를 물을 것이 없고, 잘못 찍었으면 다시 누르면 꺼진다.
 *
 * <p>무효화는 공정표만이다. CP 는 진도율·공정율에 아무것도 더하지 않는다 —
 * 곡선과 롤업까지 다시 받으면 색 하나 바꾸는 데 네 요청이 나간다.
 */
export function useWbsCriticalPath(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, on }: { itemId: string; on: boolean }) =>
      apiFetch<PmisWbsItem>(`/v1/pmis/wbs/${itemId}/critical-path`, {
        method: 'PUT',
        body: { criticalPath: on },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
    },
  });
}

/**
 * <b>목표</b> 일정 한 줄 (V539 · 사용자 확정 2026-10-23). 둘 다 비우면 목표를 지운다.
 *
 * <p>무효화는 공정표만이다 — 목표는 진도율·곡선·롤업에 들어가지 않는다(그것들은 계약이 기준이다).
 */
export function useWbsTarget(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, targetStartDate, targetEndDate }: {
      itemId: string;
      targetStartDate: string | null;
      targetEndDate: string | null;
    }) =>
      apiFetch<PmisWbsItem>(`/v1/pmis/wbs/${itemId}/target`, {
        method: 'PUT',
        body: { targetStartDate, targetEndDate },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
    },
  });
}

/**
 * 공정 <b>연계</b> 를 잇고 끊는다 (V276 · 사용자 지시 2026-09-15).
 *
 * <p>무효화는 공정표만이다 — 연계는 날짜도 진도율도 옮기지 않는다(그 일은 사람이 한다).
 */
export function useWbsLinks(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
  };
  return {
    add: useMutation({
      mutationFn: (payload: { predecessorId: string; successorId: string }) =>
        apiFetch<PmisWbsLink>('/v1/pmis/wbs/links', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/wbs/links/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
}

export function useWbsSheet(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.wbsSheet(siteId),
    queryFn: () => apiFetch<PmisWbsSheet>(`/v1/pmis/wbs/sheet?siteId=${siteId}`),
    enabled: Boolean(siteId),
  });
}

/**
 * 공정표 엑셀 — 서버가 현장 서식(`docs/samples/wbs`)으로 굽는다 (DOC-86). 링크로 곧장 내려받는다.
 * `blank` 는 공정 줄을 비운 <b>양식</b>이다 — 채워서 [엑셀 가져오기] 로 돌려보내면 고정 자리로 읽힌다.
 */
export const wbsExportUrl = (siteId: string, blank = false) =>
  `/api/v1/pmis/wbs/export.xlsx?siteId=${siteId}${blank ? '&blank=true' : ''}`;

/** 엑셀에서 읽은 공정 목록을 한 번에 등록한다(DOC-33). 파일이 아니라 <b>줄</b>이 올라간다. */
export type WbsImportPayload = {
  siteId: string;
  rows: Array<{
    code: string;
    name: string;
    parentCode: string | null;
    weight: number | null;
    plannedQuantity: number | null;
    /** 대표물량의 항목 — 「무엇의 수량인가」(V254) */
    quantityLabel: string | null;
    unit: string | null;
    plannedStartDate: string | null;
    plannedEndDate: string | null;
    /** 공정표의 `금 액 [천원]`. `weight` 와 같은 값이지만 배분표는 금액 열을 따로 본다(V164). */
    costAmount: number | null;
    /** 이름이 적힌 칸(0-기준 · V253). 가운데 칸을 비운 줄이 한 칸 당겨지지 않게 그대로 보낸다. */
    levelDepth?: number | null;
  }>;
  /**
   * 공정표 맨 아래 <b>누계 계획공정율 행</b>에서 읽은 곡선 (DOC-33).
   * 있으면 그것이 예정 곡선의 정본이다 — 착수·완료일로 S-curve 를 지어내지 않는다.
   */
  plannedCurve?: Array<{ date: string; rate: number }>;
  /** 공정표의 <b>실적</b> 곡선. 계획 바로 아래 줄에 적혀 있다(DOC-33). */
  actualCurve?: Array<{ date: string; rate: number }>;
};

export type WbsImportResult = {
  created: number;
  /** 이름 자리가 같아 <b>값을 고친</b> 공정의 코드들 (DOC-86 §5.1). 건너뛰지 않고 합친다. */
  updated: string[];
  orphanCodes: string[];
  /** 파일에서 읽어 저장한 계획 곡선 지점 수. 0 이면 곡선 행이 없었다. */
  curvePoints: number;
};

/**
 * 공정표 금액 단위 (V565) — 1 원 · 1,000 천원 · 10,000 만원 · 1,000,000 백만원 · 100,000,000 억원.
 * 저장된 금액은 파일의 수 그대로이고, 서버가 이 단위를 곱해 원으로 내준다.
 */
export const WBS_AMOUNT_UNITS: Array<{ value: number; label: string }> = [
  { value: 1, label: '원' },
  { value: 1_000, label: '천원' },
  { value: 10_000, label: '만원' },
  { value: 1_000_000, label: '백만원' },
  { value: 100_000_000, label: '억원' },
];

const wbsAmountUnitKey = (siteId: string) => ['pmis', 'wbs-amount-unit', siteId] as const;

/** 그 현장의 공정표 금액 단위. 아직 안 정했으면 서버가 계약금액과 견주어 짚은 값이다. */
export function useWbsAmountUnit(siteId: string) {
  return useQuery({
    queryKey: wbsAmountUnitKey(siteId),
    queryFn: () => apiFetch<number>(`/v1/pmis/wbs/amount-unit?siteId=${siteId}`),
    enabled: Boolean(siteId),
  });
}

/** 공정표·배분표·원가관리 공종 금액이 이 단위를 곱해 읽으므로 함께 무효화한다. */
function invalidateWbsMoney(queryClient: ReturnType<typeof useQueryClient>, siteId: string) {
  void queryClient.invalidateQueries({ queryKey: wbsAmountUnitKey(siteId) });
  void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
  void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
  void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCostSpread(siteId) });
  void queryClient.invalidateQueries({ queryKey: ['office', 'sheet', siteId] });
}

export function useSaveWbsAmountUnit(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (unit: number) =>
      apiFetch<number>(`/v1/pmis/wbs/amount-unit?siteId=${siteId}&unit=${unit}`, { method: 'PUT' }),
    onSuccess: () => invalidateWbsMoney(queryClient, siteId),
  });
}

export function useWbsImport(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** `amountUnit` 은 그 파일의 금액 단위 — 실려 가면 현장의 공정표 금액 단위로 적힌다(V565). */
    mutationFn: ({ amountUnit, ...payload }: WbsImportPayload & { amountUnit?: number }) =>
      apiFetch<WbsImportResult>(
        `/v1/pmis/wbs/import${amountUnit == null ? '' : `?amountUnit=${amountUnit}`}`,
        { method: 'POST', body: payload },
      ),
    onSuccess: () => {
      invalidateWbsMoney(queryClient, siteId);
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
      // 가져온 줄이 공정표 화면에 바로 서야 한다 — 창을 닫으면 그 표가 이미 새 자료다.
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
      // 공정을 새로 넣으면 계획 일정이 생긴다 — 예정 곡선이 바로 그려져야 한다.
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCostSpread(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
    },
  });
}

/** 가져온 공정 되돌리기 결과. */
export type WbsRevertResult = {
  deleted: number;
  /** 진척이 쌓여 있어 <b>남긴</b> 공정 수. 지우면 그 기록까지 사라진다. */
  keptWithProgress: number;
  curvePointsCleared: number;
};

/**
 * <b>엑셀로 가져온 공정 되돌리기.</b> 손으로 만든 공정과 진척이 쌓인 공정은 남는다.
 *
 * <p>한 번에 수십~수백 줄이 들어가는데 되돌릴 방법이 없으면, 잘못 넣었을 때 손으로 하나씩
 * 지워야 한다 — 그건 되돌릴 수 없는 것과 같다(사용자 지적 2026-08-10).
 */
export function useWbsRevertImport(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<WbsRevertResult>(`/v1/pmis/wbs/imported?siteId=${siteId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCostSpread(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
    },
  });
}

export type WbsClearResult = {
  deleted: number;
  /** 함께 지운 일보 실적 줄 수. 일보 글은 남는다. */
  dailyLogLines: number;
  curvePointsCleared: number;
};

/**
 * <b>공정표 전체 삭제</b> — 손으로 만든 줄·진척이 쌓인 줄까지 전부(사용자 지시 2026-10-28).
 * 500줄을 하나씩 지우게 두지 않는다. 최종 저장된 공정표는 서버가 막는다.
 */
export function useWbsDeleteAll(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<WbsClearResult>(`/v1/pmis/wbs/all?siteId=${siteId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsSheet(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCostSpread(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.dailyLogs(siteId) });
      void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
    },
  });
}

/**
 * AI 가 판단한 시트 <b>지도</b> (DOC-33). 번호는 0부터, 없으면 -1.
 *
 * <p>값은 하나도 들어 있지 않다 — 값 읽기는 브라우저가 이 지도를 따라 한다.
 * 그래야 같은 파일이 늘 같은 결과가 된다.
 */
export type PmisSheetLayout = {
  available: boolean;
  headerRow: number;
  /** 공정 데이터가 시작하는 줄. 머리글이 여러 줄인 파일이 있다. 없으면 -1. */
  dataStartRow: number;
  levelColumns: number[];
  weightColumn: number;
  /** 구성비(보할%) 열. 미리보기에서 금액과 나란히 대조한다. */
  shareColumn: number;
  quantityColumn: number;
  /** 대표물량의 <b>항목</b> 열(V254). 옛 응답에는 없어 undefined 일 수 있다 */
  quantityLabelColumn?: number;
  unitColumn: number;
  startColumn: number;
  endColumn: number;
  ganttFrom: number;
  dataEndRow: number;
  /** 공정 목록 <b>안</b>에 섞인 공정이 아닌 줄(이정표·소계). */
  skipRows: number[];
  curveRow: number;
  curveIsCumulative: boolean;
  /** <b>실적</b> 공정율 누계 줄. 계획 바로 아래에 있다. 없으면 -1. */
  actualCurveRow: number;
  actualIsCumulative: boolean;
  periodHeaderRow: number;
  note: string;
};

/**
 * 공정표 구조를 AI 에게 물어본다. <b>아무것도 저장하지 않는다</b> — 지도만 받는다.
 *
 * <p>실패해도 던지지 않는다. `available=false` 면 화면이 규칙 기반 판정으로 물러선다 —
 * AI 가 꺼져 있다고 가져오기가 막히면 안 된다.
 */
export async function fetchSheetLayout(input: {
  sheetName: string;
  rowCount: number;
  columnCount: number;
  text: string;
}): Promise<PmisSheetLayout | null> {
  try {
    return await apiFetch<PmisSheetLayout>('/v1/pmis/wbs/layout', { method: 'POST', body: input });
  }
  catch {
    return null;
  }
}

/** 한 공정의 진척 이력(최근순). 추이·소급 수정용이라 펼칠 때만 읽는다. */
export function useWbsProgressHistory(itemId: string | null) {
  return useQuery({
    queryKey: ['pmis', 'wbs-progress', itemId],
    queryFn: () => apiFetch<PmisWbsProgress[]>(`/v1/pmis/wbs/${itemId}/progress`),
    enabled: !!itemId,
  });
}

// ── 작업일보 ─────────────────────────────────────────────────────────────────

/** 일보에 실어 보내는 실적 줄. 저장은 본문 전체 저장이라 화면이 늘 현재 줄 <b>전체</b>를 보낸다. */
export type DailyLogItemPayload = {
  wbsItemId: string;
  quantity: number | null;
  unit: string | null;
  progressRate: number | null;
  note: string | null;
};

export type DailyLogPayload = Omit<
  PmisDailyLog,
  'id' | 'laborTotal' | 'createdAt' | 'status' | 'submittedAt' | 'items'
> & { items: DailyLogItemPayload[] };

export function useDailyLogs(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.dailyLogs(siteId),
    queryFn: () => apiFetch<PmisDailyLog[]>(`/v1/pmis/daily-logs?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 그 현장·그 날짜의 관측 날씨. 일보를 열 때 <b>칸에 미리 채우려고</b> 부른다(DOC-32).
 *
 * `available=false` 여도 칸은 잠기지 않는다 — <b>받아오지 못한 것</b>과 <b>적으면 안 되는 것</b>은 다르다.
 * 오늘 날짜일 때만 부른다. 지난 날짜는 관측을 되돌려 받을 수 없어 서버가 어차피 사유만 돌려준다.
 */
export function useSiteWeather(siteId: string, date: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['pmis', 'weather', siteId, date],
    queryFn: () => apiFetch<PmisSiteWeather>(`/v1/pmis/daily-logs/weather?siteId=${siteId}&date=${date}`),
    enabled: enabled && !!siteId && !!date,
    // 날씨는 10분이면 충분히 새 값이다(서버 캐시와 같은 간격). 모달을 여닫을 때마다 부르지 않는다.
    staleTime: 10 * 60 * 1000,
    // 못 받아온 것도 답이다("직접 적어 주세요"). 재시도로 모달을 붙잡아 두지 않는다.
    retry: false,
  });
}

/**
 * 제출 프리뷰 — "이 일보를 반영하면 무엇이 얼마가 되는가". 서버는 아무것도 쓰지 않는다.
 *
 * 캐시하지 않는다(`staleTime: 0`) — 공정율은 다른 사람이 그 사이에 고칠 수 있고, 낡은 프리뷰를 보고
 * 누른 [제출] 은 사람이 본 것과 다른 결과를 낸다.
 */
export function useDailyLogPreview(logId: string | null) {
  return useQuery({
    queryKey: ['pmis', 'daily-log-preview', logId],
    queryFn: () => apiFetch<PmisProgressPreview>(`/v1/pmis/daily-logs/${logId}/progress-preview`),
    enabled: !!logId,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDailyLogMutations(siteId: string) {
  const queryClient = useQueryClient();
  // 출역 합계가 일보 응답에 실려 오므로, 출역을 고치면 일보도 다시 읽어야 한다(그 반대도 같다).
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: pmisKeys.dailyLogs(siteId) });
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'labor', siteId] });
  };
  // 제출·제출취소는 공정율까지 움직인다 — 공정율 탭과 현장 카드가 함께 낡는다.
  const invalidateWithProgress = () => {
    invalidate();
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbs(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.wbsCurve(siteId) });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.rollups });
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'daily-log-preview'] });
  };

  return {
    create: useMutation({
      mutationFn: (payload: DailyLogPayload) =>
        apiFetch<PmisDailyLog>('/v1/pmis/daily-logs', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: DailyLogPayload }) =>
        apiFetch<PmisDailyLog>(`/v1/pmis/daily-logs/${id}`, { method: 'PUT', body: payload }),
      // 제출된 일보를 고치면 공정율도 따라 고쳐진다(서버가 재계산한다).
      onSuccess: invalidateWithProgress,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/daily-logs/${id}`, { method: 'DELETE' }),
      onSuccess: invalidateWithProgress,
    }),
    /** 제출 — 이 순간 실적이 공정율로 접힌다. 수기 값과 충돌한 공정은 `skipped` 로 돌아온다. */
    submit: useMutation({
      mutationFn: ({ id, overwriteManual }: { id: string; overwriteManual: boolean }) =>
        apiFetch<{ log: PmisDailyLog; skipped: string[] }>(`/v1/pmis/daily-logs/${id}/submit`, {
          method: 'POST',
          body: { overwriteManual },
        }),
      onSuccess: invalidateWithProgress,
    }),
    unsubmit: useMutation({
      mutationFn: (id: string) =>
        apiFetch<PmisDailyLog>(`/v1/pmis/daily-logs/${id}/unsubmit`, { method: 'POST' }),
      onSuccess: invalidateWithProgress,
    }),
  };
}

// ── 출역일보 ─────────────────────────────────────────────────────────────────

export type LaborPayload = Omit<PmisLaborRecord, 'id' | 'sortOrder'>;

/** @param date null 이면 그 현장의 전체 기간(최근순) */
export function useLaborRecords(siteId: string, date: string | null) {
  return useQuery({
    queryKey: pmisKeys.labor(siteId, date),
    queryFn: () =>
      apiFetch<PmisLaborRecord[]>(`/v1/pmis/labor?siteId=${siteId}${date ? `&date=${date}` : ''}`),
    enabled: !!siteId,
  });
}

export function useLaborMutations(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'labor', siteId] });
    void queryClient.invalidateQueries({ queryKey: pmisKeys.dailyLogs(siteId) });
  };

  return {
    create: useMutation({
      mutationFn: (payload: LaborPayload) =>
        apiFetch<PmisLaborRecord>('/v1/pmis/labor', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: LaborPayload }) =>
        apiFetch<PmisLaborRecord>(`/v1/pmis/labor/${id}`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/labor/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
}

// ── 기성 ─────────────────────────────────────────────────────────────────────

/**
 * 저장할 때 보내는 값 — 보기 링크는 <b>빠진다</b>.
 *
 * <p>presigned URL 은 서버가 매번 새로 계산해 내려 주는 값이라 되돌려 보낼 것이 없다(키는 보낸다).
 */
export type PaymentPayload = Omit<PmisPaymentClaim, 'id' | 'invoiceFileUrl' | 'receiptFileUrl'> & {
  /**
   * 세금계산서가 <b>처음 붙는</b> 이 저장에서 현장에 앱 푸시를 보낼지 (사용자 지시 2026-09-29).
   *
   * <p>응답에는 없는 칸이라 {@link PmisPaymentClaim} 에서 나오지 않는다 — <b>보낼 때만</b> 쓰는
   * 값이고, 저장된 뒤에 되돌려 받을 것이 없다(한 번 간 알림은 행으로 남지 않는다).
   *
   * <p>안 실어 보내면 서버가 <b>보낸다</b>로 읽는다. 그래서 표의 격자 편집처럼 이 칸을 모르는
   * 자리는 그대로 두면 된다 — 거기서는 첨부가 바뀌지 않아 알림 자체가 일어나지 않는다.
   */
  pushNotify?: boolean;
};

/** 도급·하도급을 함께 읽고 화면이 갈라 그린다 — 탭을 오갈 때마다 다시 부르지 않기 위해. */
export function usePaymentClaims(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.payments(siteId),
    queryFn: () => apiFetch<PmisPaymentClaim[]>(`/v1/pmis/payments?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

export function usePaymentMutations(siteId: string) {
  const queryClient = useQueryClient();
  // 수금이 바뀌면 공무 표의 실수금도 따라 움직인다(PaymentCollectSync) — 그 화면도 다시 읽는다.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: pmisKeys.payments(siteId) });
    queryClient.invalidateQueries({ queryKey: ['office', 'sheet', siteId] });
  };

  return {
    create: useMutation({
      mutationFn: (payload: PaymentPayload) =>
        apiFetch<PmisPaymentClaim>('/v1/pmis/payments', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: PaymentPayload }) =>
        apiFetch<PmisPaymentClaim>(`/v1/pmis/payments/${id}`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/payments/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    /** 세금계산서·입금증 업로드. 파일은 서버를 지나지 않고 저장소로 바로 간다. */
    upload: (file: File, fileName: string) =>
      uploadToStorage('/v1/pmis/payments/attachments/uploads', siteId, file, fileName),
    /**
     * 수금예정일의 계산 규칙 — <b>이 탭에서 바로</b> 넣는다(사용자 지시 2026-09-14).
     *
     * <p>값이 앉는 자리는 공무 설정이라 그쪽 캐시도 함께 비운다 — 원가관리 탭을 띄워 둔 채
     * 여기서 고치는 일이 실제로 흔하다.
     */
    saveCollectTerm: useMutation({
      mutationFn: (body: { collectBase: string | null; collectOffsetDays: number | null }) =>
        apiFetch<void>(`/v1/pmis/payments/collect-term?siteId=${siteId}`, { method: 'PUT', body }),
      onSuccess: invalidate,
    }),
  };
}

// ── 검측 ─────────────────────────────────────────────────────────────────────

export type InspectionPayload = Omit<
  PmisInspection,
  'id' | 'attachmentUrl' | 'wbsCode' | 'wbsName' | 'roundNo' | 'parentId' | 'photos' | 'events' | 'siteName'
>;

/** 사진 등록 — 파일은 presign 으로 먼저 올리고 키·EXIF·해시만 보낸다. */
export type InspectionPhotoPayload = {
  objectKey: string;
  fileName: string | null;
  shotAt: string | null;
  lat: number | null;
  lon: number | null;
  qualityProfile: string | null;
  /** OVERVIEW/CLOSEUP/MEASURE/LABEL/ETC — 촬영 가이드가 무엇을 찍었는지. */
  shotKind: string;
  /** dHash 64bit hex. 브라우저가 압축 전 이미지에서 만든다(서버에는 계산할 바이트가 없다). */
  phash: string | null;
};

/** 과거 유사 지적 한 줄 — "지난번엔 이걸 지적했다". */
export type PmisSimilarDefect = {
  id: string;
  inspectionDate: string | null;
  title: string;
  trade: string | null;
  inspectionType: string | null;
  defectType: string | null;
  resultNote: string | null;
};

/** 받아쓴 말 → 지적사항 초안(L1). `available=false` 면 화면은 원문을 그대로 쓴다. */
export type PmisNoteDraft = {
  available: boolean;
  note: string | null;
  defectType: string | null;
  message: string | null;
};

/** 결함 유형. 서버 CHECK(V49)·PmisCodes 와 한 벌이다. */
export const DEFECT_TYPES = ['누락', '치수', '오염', '손상', '시공순서', '자재불량', '안전', '기타'] as const;

/** 촬영 컷 가이드. 강제하지 않는다 — 계측 사진이 없다고 막으면 아무 사진이나 계측이 된다. */
export const SHOT_KINDS = [
  { key: 'OVERVIEW', label: '전경' },
  { key: 'CLOSEUP', label: '근경' },
  { key: 'MEASURE', label: '계측' },
  { key: 'LABEL', label: '자재·성적서' },
  { key: 'ETC', label: '기타' },
] as const;

export function useInspections(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.inspections(siteId),
    queryFn: () => apiFetch<PmisInspection[]>(`/v1/pmis/inspections?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 카드 하나 — 사진 + 회차 이력 + <b>증빙 무결성 경고</b>. 드로어를 열 때만 부른다.
 *
 * 목록에는 이력도 경고도 실리지 않는다(사슬을 다시 읽어야 한다).
 */
export function useInspection(id: string | null) {
  return useQuery({
    queryKey: ['pmis', 'inspection', id],
    queryFn: () => apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}`),
    enabled: !!id,
  });
}

/** "지난번엔 이걸 지적했다" — 같은 현장·공종·구분의 과거 불합격. GPU 를 쓰지 않는다. */
export function useSimilarDefects(id: string | null) {
  return useQuery({
    queryKey: ['pmis', 'inspection-similar', id],
    queryFn: () => apiFetch<PmisSimilarDefect[]>(`/v1/pmis/inspections/${id}/similar?limit=3`),
    enabled: !!id,
  });
}

/** 검측 보고서 PDF 주소. 회차별 사진·지적·판정이 한 장으로 나온다. */
export function inspectionReportUrl(id: string): string {
  return `/api/v1/pmis/inspections/${id}/report`;
}

/**
 * 나에게 배정된 진행 중 검측. <b>현장을 가로지른다</b> — 담당자는 현장을 고르기 전에
 * "오늘 내가 볼 것" 부터 본다. 어느 현장까지 보이는지는 서버가 소속으로 판정한다.
 */
export function useMyInspections() {
  return useQuery({
    queryKey: ['pmis', 'inspections', 'mine'],
    queryFn: () => apiFetch<PmisInspection[]>('/v1/pmis/inspections/mine'),
  });
}

export function useInspectionMutations(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: pmisKeys.inspections(siteId) });
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'inspection'] });
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'inspections', 'mine'] });
  };

  return {
    create: useMutation({
      mutationFn: (payload: InspectionPayload) =>
        apiFetch<PmisInspection>('/v1/pmis/inspections', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: InspectionPayload }) =>
        apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/inspections/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    /** 착수 — 요청 상태의 건을 담당자가 집어 든다. */
    start: useMutation({
      mutationFn: (id: string) =>
        apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}/start`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    /** 합불 판정. 불합격에는 지적사항이 필수다(서버가 막는다). */
    judge: useMutation({
      mutationFn: ({
        id,
        verdict,
        note,
        defectType,
      }: {
        id: string;
        verdict: 'PASSED' | 'FAILED';
        note: string | null;
        defectType: string | null;
      }) =>
        apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}/verdict`, {
          method: 'POST',
          body: { verdict, note, defectType },
        }),
      onSuccess: invalidate,
    }),
    /**
     * 받아쓴 말 → 지적사항 문장 초안. <b>아무것도 저장하지 않는다</b> — 제안일 뿐이다.
     * 그래서 mutation 이지만 캐시를 무효화하지 않는다.
     */
    noteDraft: useMutation({
      mutationFn: ({ id, rawText }: { id: string; rawText: string }) =>
        apiFetch<PmisNoteDraft>(`/v1/pmis/inspections/${id}/note-draft`, {
          method: 'POST',
          body: { rawText },
        }),
    }),
    /** 재검 요청 — 앞 회차를 덮지 않고 다음 회차 카드를 만든다. */
    recheck: useMutation({
      mutationFn: ({ id, note, dueDate }: { id: string; note: string | null; dueDate: string | null }) =>
        apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}/recheck`, {
          method: 'POST',
          body: { note, dueDate, inspectorId: null },
        }),
      onSuccess: invalidate,
    }),
    addPhoto: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: InspectionPhotoPayload }) =>
        apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}/photos`, { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    removePhoto: useMutation({
      mutationFn: ({ id, photoId }: { id: string; photoId: string }) =>
        apiFetch<PmisInspection>(`/v1/pmis/inspections/${id}/photos/${photoId}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    /** 검측 첨부·사진 업로드. 파일은 서버를 지나지 않고 저장소로 바로 간다. */
    upload: (siteId2: string, file: File | Blob, fileName: string) =>
      uploadToStorage('/v1/pmis/inspections/attachments/uploads', siteId2, file, fileName),
  };
}

// ── 문서·자료 ────────────────────────────────────────────────────────────────

// ── 서류 요약(AI) ────────────────────────────────────────────────────────────

/**
 * 서류가 앉는 자리 — <b>AI 요약이 자리마다 한 장</b>이다(V95).
 *
 * <p>값은 화면의 탭 키가 아니라 서버·DB CHECK 와 맞춘 이름이다. 자리를 안 밝히면 서버가 계약서로
 * 본다(슬롯이 생기기 전 호출과의 약속).
 */
export type DocSummarySlot = 'CONTRACT' | 'PERMIT' | 'SUBCONTRACT' | 'PAYMENT' | 'MATERIAL' | 'COMPLETION';

/** 서류에서 뽑아낸 항목 하나. `label` 은 에이전트의 스키마가 준 이름이다(없으면 `key`). */
export type ContractSummaryField = {
  key: string;
  label: string | null;
  value: string;
  confidence: number | null;
  /**
   * 사람이 고치거나 확정한 값인가(주요사항 표기관리 · 2026-10-27). 확정값은 [다시 읽기] 가 덮지 않는다.
   * 서버가 아직 옛 판이면 이 칸이 오지 않는다 — 없으면 AI 값 그대로다.
   */
  confirmed?: boolean | null;
};

export type SiteContractSummary = {
  /** AI 연동이 꺼져 있으면 화면이 [AI 요약] 버튼을 내지 않는다. */
  aiEnabled: boolean;
  documentFileId: string | null;
  sourceName: string | null;
  summary: string | null;
  reason: string | null;
  confidence: number | null;
  lowConfidence: boolean;
  fields: ContractSummaryField[];
  /** 못 읽은 사정·유형이 계약서가 아니라는 경고. 있으면 화면이 그대로 적는다. */
  note: string | null;
  updatedAt: string | null;
};

/**
 * 그 <b>서류 자리</b>에 놓인 자료실 문서 (2026-08-28).
 *
 * <h2>왜 자료실 목록(`useDocumentFiles`)을 안 쓰나</h2>
 * 그쪽은 <b>자료실 조회 권한</b>({@code docs_browse})으로 열린다. 계약서를 전 직원에게 열기로
 * 하면서 그 문으로는 답이 안 나온다 — 자료실 권한을 전원에게 주면 계약서가 아니라 자료실 전체가
 * 열리고, 안 주면 계약서 탭이 빈 채로 뜬다.
 *
 * <p>이 문은 <b>자리마다</b> 열린다(계약서는 `site_pmis_contract`). 좁히는 일도 서버가 한다 —
 * 브라우저에서 걸러 봐야 응답에는 이미 다른 자리의 서류가 실려 나간 뒤다.
 *
 * <p>돌려주는 모양은 자료실 목록과 같으므로({@code DocumentFile}) 화면은 그대로 쓴다.
 */
export function useSiteDocSlotFiles(siteId: string, slot: DocSummarySlot) {
  return useQuery({
    queryKey: ['pmis', 'doc-slot-files', siteId, slot],
    queryFn: () => apiFetch<DocumentFile[]>(`/v1/pmis/sites/${siteId}/doc-slots/${slot}/files`),
    enabled: !!siteId,
  });
}

export function useSiteContractSummary(siteId: string, slot: DocSummarySlot = 'CONTRACT') {
  return useQuery({
    queryKey: pmisKeys.contractSummary(siteId, slot),
    queryFn: () => apiFetch<SiteContractSummary>(`/v1/pmis/sites/${siteId}/contract-summary?slot=${slot}`),
    enabled: !!siteId,
  });
}

/**
 * 그 자리의 서류 한 건을 읽어 요약을 다시 만든다. <b>사람이 누를 때만</b> 돈다 — 14B 추출은 GPU 큐를
 * 타고 수십 초가 걸려서, 탭을 열 때마다 자동으로 돌리면 그 시간을 매번 사람이 기다린다.
 *
 * <p>캐시는 <b>그 자리 것만</b> 갈아 끼운다. 준공 서류를 읽었다고 계약서 탭의 요약이 사라지면
 * 사람은 "AI 가 앞엣것을 지웠다" 로 읽는다.
 */
export function useAnalyzeContract(siteId: string, slot: DocSummarySlot = 'CONTRACT') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentFileId: string) =>
      apiFetch<SiteContractSummary>(`/v1/pmis/sites/${siteId}/contract-summary`, {
        method: 'POST',
        body: { documentFileId, slot },
      }),
    onSuccess: (data) => queryClient.setQueryData(pmisKeys.contractSummary(siteId, slot), data),
  });
}

/**
 * 그 자리의 요약을 <b>지운다</b>. 근거 서류를 치웠거나 엉뚱한 문서를 읽혔을 때 필요하다 —
 * 문서를 지워도 요약은 남게 되어 있어서(V87), 화면이 계속 옛 조건을 말하는 것을 끊는 길이 이것뿐이다.
 *
 * <p>지운 뒤에는 <b>다시 읽는다</b>(무효화). 캐시에 손으로 null 을 넣으면 «AI 가 켜져 있는가»
 * 같은 나머지 값까지 함께 사라져, 서류가 있는데도 [AI로 요약] 버튼이 안 나오는 상태가 된다.
 */
export function useRemoveContractSummary(siteId: string, slot: DocSummarySlot = 'CONTRACT') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/v1/pmis/sites/${siteId}/contract-summary?slot=${slot}`, { method: 'DELETE' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: pmisKeys.contractSummary(siteId, slot) }),
  });
}

/**
 * 주요사항 표기관리 — 사람이 값을 고치거나 확정한다(2026-10-27). 보낸 항목만 손대고, 보낸 항목은
 * 모두 확정이 된다. 확정값은 [다시 읽기] 가 덮지 않는다(서버 `keepConfirmed`).
 */
export function useEditContractSummaryFields(siteId: string, slot: DocSummarySlot = 'CONTRACT') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: Array<{ key: string; label: string | null; value: string }>) =>
      apiFetch<SiteContractSummary>(`/v1/pmis/sites/${siteId}/contract-summary/fields`, {
        method: 'PUT',
        body: { slot, fields },
      }),
    onSuccess: (data) => queryClient.setQueryData(pmisKeys.contractSummary(siteId, slot), data),
  });
}

export function useSiteContacts(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.contacts(siteId),
    queryFn: () => apiFetch<PmisSiteContact[]>(`/v1/pmis/contacts?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 연락처 수첩 쓰기 — <b>외부 업체 담당자만</b>(V98).
 *
 * 관계사·우리 인원 줄은 서버가 `editable:false` 로 내려보내고, 화면은 그 줄에 버튼을 그리지 않는다.
 * 서버도 그 줄의 id 를 아예 만들지 않으므로 화면 판정을 우회할 대상 자체가 없다.
 */
export function useSiteContactMutations(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: pmisKeys.contacts(siteId) });

  return {
    create: useMutation({
      mutationFn: (payload: SiteContactPayload) =>
        apiFetch<PmisSiteContact>('/v1/pmis/contacts', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: SiteContactPayload }) =>
        apiFetch<PmisSiteContact>(`/v1/pmis/contacts/${id}`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/contacts/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
}

export function useSiteDocuments(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.documents(siteId),
    queryFn: () => apiFetch<PmisSiteDocument[]>(`/v1/pmis/documents?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * 현장 문서(옛 등록)를 <b>바로 지우는 사람</b>인가 — 전산 담당·최고관리자. 서버의
 * {@code SiteDocumentService.decidesDeletes} 와 같은 판정이다. 화면은 확인 문구를 고르는 데만 쓰고,
 * 지울지 요청할지는 서버가 정한다.
 */
export function useDecidesSiteDocDeletes(): boolean {
  const { data: session } = useSession();
  const roles = session?.user?.roles ?? [];
  return roles.includes('sys_admin') || roles.includes('SUPER_ADMIN');
}

export function useSiteDocumentMutations(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: pmisKeys.documents(siteId) });

  return {
    /** 업로드 → 등록을 한 번에. 저장소가 꺼져 있으면 업로드 단계에서 준비중(503)으로 막힌다. */
    add: useMutation({
      mutationFn: async ({ file, docType }: { file: File; docType: string | null }) => {
        const { key } = await uploadToStorage('/v1/pmis/documents/uploads', siteId, file);
        return apiFetch<PmisSiteDocument>('/v1/pmis/documents', {
          method: 'POST',
          body: { siteId, name: file.name, docType, objectKey: key },
        });
      },
      onSuccess: invalidate,
    }),
    /**
     * 삭제 <b>요청</b>을 낸다 (V378 · 사용자 지시 2026-10-01).
     *
     * <p>길은 그대로 DELETE 다 — 서버가 지우지 않고 그 줄을 숨기고 전산 담당에게 할일·푸시를
     * 보낸다. 그래서 목록에서는 사라진 것처럼 보이고, 실제로 지워지는 것은 승인 뒤다.
     * 전산 담당·최고관리자가 누르면 바로 지운다({@link useDecidesSiteDocDeletes} · 2026-10-22).
     */
    remove: useMutation({
      mutationFn: (id: string) => apiFetch<void>(`/v1/pmis/documents/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    /** 삭제 요청 승인 — 이때 지워진다. 전산 담당만 서버가 받는다. */
    approveDelete: useMutation({
      mutationFn: (id: string) =>
        apiFetch<void>(`/v1/pmis/documents/${id}/delete-requests/approve`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    /** 삭제 요청 반려 — 그 줄이 목록에 다시 선다. */
    rejectDelete: useMutation({
      mutationFn: (id: string) =>
        apiFetch<void>(`/v1/pmis/documents/${id}/delete-requests/reject`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
  };
}

// ── 전경사진 (V163) ─────────────────────────────────────────────────────────

/**
 * 현장 전경사진 한 장.
 *
 * <p>`url` 은 <b>만료되는 링크</b>다(서버가 조회할 때 서명한다). 저장소가 꺼져 있거나 원본이
 * 사라졌으면 null 이고, 화면은 그 자리를 「열 수 없는 사진」으로 표시한다 — 깨진 이미지 아이콘을
 * 그대로 두면 사용자는 자기 인터넷을 의심한다.
 *
 * <p>`takenAt` 은 <b>서버가 정한 한 값</b>이다(EXIF 촬영시각이 없으면 올린 날). 화면에서
 * `shotAt ?? createdAt` 을 되풀이하면 목록·월 묶음·공유 문구가 조용히 어긋난다.
 */
export type PmisSceneryPhoto = {
  id: string;
  siteId: string;
  fileName: string | null;
  caption: string | null;
  url: string | null;
  /** EXIF 촬영시각. 카카오톡을 거쳐 온 사진에는 없다. */
  shotAt: string | null;
  takenAt: string;
  /** 전경(SCENERY)인가 시공(CONSTRUCTION)인가 — 화면이 좌우로 가르는 기준(V240). */
  photoKind: SceneryPhotoKind;
  /**
   * 이 사진이 속한 주의 월요일(`YYYY-MM-DD` · V240).
   *
   * <p>★ `takenAt` 으로 <b>다시 계산하지 말 것</b>. EXIF 없는 사진은 「올린 날」이 촬영시각을
   * 대신하므로, 지난주에 찍어 이번 주에 올린 사진이 이번 주 것으로 앉는다 — 그 어긋남을 없애려고
   * 서버가 값으로 들고 있는 칸이다. 여기서 다시 세면 그 결정이 되돌아온다.
   */
  weekOf: string;
  createdAt: string;
};

/**
 * 전경사진 탭이 받는 두 갈래 (V240 · 사용자 지시 2026-09-07).
 *
 * <p>전경은 <b>바깥에서 본 현장</b>이고 시공은 <b>안에서 찍은 그 주의 작업</b>이다. 나눈 실제
 * 이유는 영상화다 — 전경만 이어야 건물이 올라가는 것이 보이고, 실내 사진이 한 장 끼면 그
 * 프레임에서 화면이 튄다.
 */
export type SceneryPhotoKind = 'SCENERY' | 'CONSTRUCTION';

export const SCENERY_KIND_LABEL: Record<SceneryPhotoKind, string> = {
  SCENERY: '전경사진',
  CONSTRUCTION: '시공사진',
};

export function useSceneryPhotos(siteId: string) {
  return useQuery({
    queryKey: pmisKeys.scenery(siteId),
    queryFn: () => apiFetch<PmisSceneryPhoto[]>(`/v1/pmis/sites/${siteId}/scenery`),
    enabled: !!siteId,
  });
}

/** 등록할 때 함께 싣는 것. 파일은 이미 저장소에 올라가 있고 여기에는 키만 온다. */
export type SceneryPhotoPayload = {
  objectKey: string;
  fileName: string | null;
  caption: string | null;
  /** ISO 문자열. EXIF 가 없으면 null — 서버가 올린 날로 대신한다. */
  shotAt: string | null;
  photoKind: SceneryPhotoKind;
  /**
   * 그 주의 월요일(`YYYY-MM-DD`). <b>비우면 서버가 낸다</b> — 촬영시각에서, 없으면 올린 날에서.
   *
   * <p>화면이 늘 채워 보내지 않는 것이 요점이다. 스무 장을 올리는 사람에게 주차를 묻는 순간 이
   * 창은 「분류를 묻는 창」이 되고, 그것이 이 탭이 피하려던 것이다(V163).
   */
  weekOf: string | null;
};

export function useSceneryMutations(siteId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: pmisKeys.scenery(siteId) });

  return {
    /**
     * 등록. <b>목록 무효화는 부르는 쪽이 한다</b> — 스무 장을 한 장씩 등록할 때마다 목록을 다시
     * 읽으면 스무 번의 왕복이 생기고, 그동안 화면의 격자가 계속 다시 그려진다.
     */
    add: useMutation({
      mutationFn: (payload: SceneryPhotoPayload) =>
        apiFetch<PmisSceneryPhoto>(`/v1/pmis/sites/${siteId}/scenery`, { method: 'POST', body: payload }),
    }),
    /**
     * 설명 한 줄과 <b>주차</b>. 둘을 한 부름에 둔 것은 크게 보기 창에서 사람이 고치는 것이 이
     * 둘뿐이기 때문이다 — 나누면 그 창이 저장 버튼을 두 개 갖는다.
     *
     * <p>`weekOf` 를 안 보내면 서버가 지금 값을 지킨다(그 칸은 비울 수 없다).
     */
    changeCaption: useMutation({
      mutationFn: ({ id, caption, weekOf }: { id: string; caption: string | null; weekOf?: string }) =>
        apiFetch<PmisSceneryPhoto>(`/v1/pmis/sites/${siteId}/scenery/${id}`, {
          method: 'PATCH',
          body: { caption, weekOf: weekOf ?? null },
        }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) =>
        apiFetch<void>(`/v1/pmis/sites/${siteId}/scenery/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    /**
     * 여러 장을 <b>한 번에</b> (2026-09-23 — 주차·월 묶음 전체 선택). 한 장씩 돌리면 예순 번의 왕복이고,
     * 중간에 끊기면 절반만 지워진 채 멈춘다. 돌려주는 값은 실제로 지워진 장수다.
     */
    removeMany: useMutation({
      mutationFn: (ids: string[]) =>
        apiFetch<number>(`/v1/pmis/sites/${siteId}/scenery/delete`, { method: 'POST', body: { ids } }),
      onSuccess: invalidate,
    }),
    /**
     * 사진 한 장을 저장소로. 파일 바이트가 API 를 지나지 않는다.
     *
     * <p>갈래를 함께 보낸다 — 저장소 <b>폴더가 갈리기 때문</b>이다(전경사진/ · 시공사진/).
     * 자료실에서 이 현장을 여는 사람은 탭을 거치지 않고 폴더만 보므로, 섞어 두면 그 구분이
     * 화면에서만 존재하는 것이 된다.
     */
    upload: (file: File | Blob, fileName: string, photoKind: SceneryPhotoKind) =>
      uploadToStorage(`/v1/pmis/sites/${siteId}/scenery/uploads`, siteId, file, fileName, { photoKind }),
    /** 여러 장을 다 올린 뒤 <b>한 번만</b> 목록을 다시 읽는다. */
    refresh: invalidate,
  };
}

// ── 현장 대화방(오픈채팅 허브) ───────────────────────────────────────────────

/**
 * 그 현장의 오픈채팅방 목록. `status` 를 비우면 폐기된 방까지 함께 온다.
 *
 * 목록에는 <b>입장 비밀번호가 실리지 않는다</b> — 필요할 때 상세를 따로 읽는다.
 */
export function useOpenChatRooms(siteId: string, status: PmisOpenChatStatus | null = 'ACTIVE') {
  return useQuery({
    queryKey: pmisKeys.openChat(siteId, status),
    queryFn: () =>
      apiFetch<PmisOpenChatRoom[]>(
        `/v1/pmis/openchat-rooms?siteId=${siteId}${status ? `&status=${status}` : ''}`,
      ),
    enabled: !!siteId,
  });
}

/** 방 하나의 상세 — 입장 비밀번호가 필요한 자리에서만 부른다(펼쳤을 때). */
export function useOpenChatRoom(id: string | null) {
  return useQuery({
    queryKey: ['pmis', 'openchat-room', id],
    queryFn: () => apiFetch<PmisOpenChatRoom>(`/v1/pmis/openchat-rooms/${id}`),
    enabled: !!id,
  });
}

/**
 * 현장을 걸친 대화방 목록 — 홈 위젯과 대화방 목록 화면(`/sites/chats`)이 같이 쓴다.
 *
 * 본사·관리자는 현장을 가리지 않고 전부, 현장 소속자는 자기 현장 것만 온다(서버 판정).
 * `status` 를 비우면 내려간 방까지 함께 온다.
 */
export function useMyOpenChatRooms(status: PmisOpenChatStatus | null = 'ACTIVE') {
  return useQuery({
    queryKey: [...pmisKeys.openChatMine, status ?? 'all'],
    queryFn: () =>
      apiFetch<PmisOpenChatRoom[]>(`/v1/pmis/openchat-rooms/mine${status ? `?status=${status}` : ''}`),
  });
}

/**
 * 현장별 대화방 개수. 카드마다 목록을 질의하지 않도록 한 번에 받는다(공정율 롤업과 같은 방식).
 *
 * 권한 키가 `site_status`(요약)라 현장 조회 권한이 없는 사람에게도 열린다.
 */
export function useOpenChatCounts() {
  return useQuery({
    queryKey: pmisKeys.openChatCounts,
    queryFn: () => apiFetch<PmisSiteRoomCount[]>('/v1/pmis/openchat-rooms/counts'),
  });
}

export function useOpenChatMutations(siteId: string) {
  const queryClient = useQueryClient();
  // 방을 고치면 그 현장 목록과, 카드 배지·홈 위젯이 함께 달라진다.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'openchat'] });
    void queryClient.invalidateQueries({ queryKey: ['pmis', 'openchat-room'] });
  };

  return {
    create: useMutation({
      mutationFn: (payload: OpenChatRoomPayload) =>
        apiFetch<PmisOpenChatRoom>('/v1/pmis/openchat-rooms', { method: 'POST', body: payload }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: OpenChatRoomPayload }) =>
        apiFetch<PmisOpenChatRoom>(`/v1/pmis/openchat-rooms/${id}`, { method: 'PUT', body: payload }),
      onSuccess: invalidate,
    }),
    /** 폐기. 지우지 않고 내린다 — 삭제 경로는 서버에 없다. */
    archive: useMutation({
      mutationFn: (id: string) =>
        apiFetch<PmisOpenChatRoom>(`/v1/pmis/openchat-rooms/${id}/archive`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    report: useMutation({
      mutationFn: ({ id, reason }: { id: string; reason: string }) =>
        apiFetch<void>(`/v1/pmis/openchat-rooms/${id}/report`, { method: 'POST', body: { reason } }),
      onSuccess: invalidate,
    }),
    siteId,
  };
}

/**
 * 카카오톡 오픈채팅 링크 형식. <b>서버 정규식·DB CHECK 와 같은 값</b>이다(DOC-26 §3.1).
 *
 * 세 곳에 같은 식이 있는 것은 중복이 아니라 층이다 — 화면은 붙여넣는 순간 알려주고,
 * 서버는 화면을 거치지 않은 요청을 막고, DB 는 마지막으로 잘못된 행이 남지 않게 한다.
 */
export const OPEN_CHAT_URL_PATTERN = /^https:\/\/open\.kakao\.com\/o\/[A-Za-z0-9]+$/;

/** 현장 대표 이미지 지정·해제. 지우려면 파일 없이 부른다. */
export function useChangeSitePhoto(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File | null) => {
      // 문서 업로드와 다른 경로를 쓴다 — 대표 이미지는 `hr/{현장명}/대표사진/…` 으로 들어가, 저장소를
      // 열어 봐도 어느 현장 사진인지 알아볼 수 있다(문서 업로드 키는 `hr/{uuid}/…` 라 알아볼 수 없다).
      const objectKey = file
        ? (await uploadToStorage(`/v1/pmis/sites/${siteId}/photo/uploads`, siteId, file)).key
        : null;
      return apiFetch<string | null>(`/v1/pmis/sites/${siteId}/photo`, { method: 'PUT', body: { objectKey } });
    },
    // 현장 목록이 대표 이미지를 실어 오므로 목록을 다시 읽는다(카드 사진이 바로 바뀐다).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.sites }),
  });
}

/**
 * presigned PUT 업로드. ① 서버에서 링크를 받고 ② 저장소로 파일을 직접 PUT 한다.
 *
 * 파일 바이트가 API 를 지나지 않으므로 큰 도면·사진이 백엔드 스레드를 물지 않는다.
 * PUT 은 저장소로 직접 가는 요청이라 `apiFetch`(세션·CSRF)를 쓰지 않는다.
 */
async function uploadToStorage(
  presignPath: string,
  siteId: string,
  file: File | Blob,
  // 압축된 사진은 Blob 이라 이름이 없다 — 부르는 쪽이 원본 이름을 넘긴다.
  fileName = file instanceof File ? file.name : 'upload',
  // 링크를 만드는 데 필요한 나머지(전경사진의 갈래 등). 안 쓰는 쪽은 그대로 두 칸만 보낸다.
  extra?: Record<string, string>,
): Promise<UploadTicket> {
  const ticket = await apiFetch<UploadTicket>(presignPath, {
    method: 'POST',
    body: { siteId, fileName, ...extra },
  });

  const response = await fetch(ticket.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!response.ok) {
    throw new Error(`파일을 저장소에 올리지 못했습니다. (HTTP ${response.status})`);
  }
  return ticket;
}

/** 현장 전체 항목 수정. 현장 관리(설정)와 같은 편집기를 현장 조회에서 쓸 때 저장 경로. */
export function useUpdateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<Site> }) => dbUpdate('sites', id, values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.sites }),
  });
}
